import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FormikValues, useFormik } from 'formik';
import { Grid } from '@mui/material';
import PrimaryButton from '../../../components/buttons/primaryButton';
import SecondaryButton from '../../../components/buttons/secondaryButton';
import { AdProviderIDs, Colors } from '../../../constants/constants';
import { useOrganizationSettingsStore } from '../../../store/organizationSettingsStore';
import { microsoftIntegrationURL } from '../../../constants/integrationConstants';
import {
  useUpdateOrganizationSettings,
} from '../../../apiHooks/mutations/organizationMutations/organizationSettingsMutations';
import {
  bingOrganizationSettingsFormValidationSchema,
} from '../../../validations/organizationSettingsFormValidationSchema';
import TextInput from '../../../components/inputs/textInput';
import ProviderNumberBoxes from '../../../components/providerNumberBoxes/providerNumberBoxes';
import useGetAdProviderDetails from '../../../apiHooks/queries/adProviderDetails/useGetAdProviderDetails';
import useGetOrganizationSettings from '../../../apiHooks/queries/useGetOrganizations';
import { GET_PROVIDER_DETAILS, ORGANIZATION_SETTINGS } from '../../../constants/pageIdentifiers';

const BingPanel = () => {
  const { organizationSettings } = useOrganizationSettingsStore();
  const { setOrganizationSettings } = useOrganizationSettingsStore();
  const { data: orgSettingsResponse } = useGetOrganizationSettings(ORGANIZATION_SETTINGS);
  const latestOrganizationSettings = orgSettingsResponse?.data?.data || organizationSettings;
  const hasToken = !!latestOrganizationSettings?.microsoftRefreshToken;

  useEffect(() => {
    if (orgSettingsResponse?.data?.data) {
      setOrganizationSettings(orgSettingsResponse.data.data);
    }
  }, [orgSettingsResponse?.data?.data, setOrganizationSettings]);

  const { data: adProviderDetailsData, isFetching } = useGetAdProviderDetails(
    GET_PROVIDER_DETAILS,
    AdProviderIDs.BING,
    hasToken,
  );

  const { t } = useTranslation();
  const {
    mutateAsync: updateOrganizationSettings,
    isLoading: isUpdatingOrganizationSettings,
  } = useUpdateOrganizationSettings();

  const formik: FormikValues = useFormik({
    initialValues: {
      microsoftAccountId: latestOrganizationSettings?.microsoftAccountId || '',
    },
    enableReinitialize: true,
    validationSchema: () => bingOrganizationSettingsFormValidationSchema(t),
    onSubmit: async (values: any) => {
      updateOrganizationSettings(values).then(() => {
        setOrganizationSettings({ ...latestOrganizationSettings, ...values });
      });
    },
  });

  if (hasToken) {
    return (
      <div>
        <div className="flex flex-1 justify-between rounded-xl border p-4">
          <form onSubmit={formik.handleSubmit} className="flex gap-4">
            <Grid item sm={12} md={6} lg={2.5}>
              <TextInput
                myKey="microsoftAccountId"
                name="microsoftAccountId"
                value={formik.values.microsoftAccountId}
                label={t('microsoftAccountId')}
                placeholder={t('microsoftAccountId')}
                size="small"
                fullWidth
                type="number"
                onChange={formik.handleChange}
                error={Boolean(formik.touched.microsoftAccountId) && Boolean(formik.errors.microsoftAccountId)}
                helperText={Boolean(formik.touched.microsoftAccountId) && formik.errors.microsoftAccountId}
              />
            </Grid>
            <Grid item sm={12} md={6} lg={2.5} gap={4}>
              <PrimaryButton
                type="submit"
                className="max-h-[40px]"
                disabled={isUpdatingOrganizationSettings}
                dataCy="bing-org-settings-submit"
              >
                {t('save')}
              </PrimaryButton>
            </Grid>
          </form>
          <div className="flex items-center justify-center gap-3 text-xs font-medium text-primary">
            <div>{t('accountStatus')}</div>
            <SecondaryButton
              style={{
                border: `1px solid ${Colors.green}`,
                color: Colors.green,
                height: '40px',
                fontSize: 14,
              }}
            >
              {t('active')}
            </SecondaryButton>
          </div>
        </div>
        <div className="mt-4 flex justify-between gap-4 rounded-xl border p-4">
          <ProviderNumberBoxes isLoading={isFetching} tracked stats={adProviderDetailsData?.data?.data?.ads_tracked || 0} />
          <ProviderNumberBoxes isLoading={isFetching} revenue stats={adProviderDetailsData?.data?.data?.revenue_tracked || 0} />
          <ProviderNumberBoxes isLoading={isFetching} leads stats={adProviderDetailsData?.data?.data?.leads_attributed || 0} />
        </div>
      </div>
    );
  }

  return (
    <div className="flex justify-between gap-5 rounded-xl border p-4">
      <div className="flex flex-col gap-2 text-primary">
        <span className="text-sm font-semibold">Connect to Bing</span>
        <p className="text-sm font-normal">
          Here you can link your Microsoft account with our platform for more efficient tracking.
        </p>
      </div>
      <button
        type="button"
        className="flex items-center gap-2.5 rounded-lg bg-primary px-5 py-2
          text-sm text-white duration-200 focus:outline-[1px] focus:outline-offset-[3px] focus:outline-primary/50 enabled:hover:bg-[#0a3a4b]
          enabled:active:bg-opacity-80 disabled:opacity-50"
        onClick={() => {
          window.location.href = microsoftIntegrationURL;
        }}
      >
        Connect to Bing
      </button>
    </div>
  );
};

export default BingPanel;
